import React from "react";
import { Button, Stack, Typography } from "@mui/material";
import {
  HandshakeOutlined,
  LockOutlined,
  TaskAltOutlined,
  TipsAndUpdatesOutlined,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";

import { RoutePaths } from "src/app/model";
import { handleBookCall } from "src/shared/utils";

// Copy lives in the i18n resources (about.values); only the icons stay here and
// are matched to the translated entries by position.
const valueIcons = [
  <TipsAndUpdatesOutlined color="primary" fontSize="large" />,
  <TaskAltOutlined color="primary" fontSize="large" />,
  <HandshakeOutlined color="primary" fontSize="large" />,
  <LockOutlined color="primary" fontSize="large" />,
];

type ValueItem = { title: string; description: string };

export const AboutUsPage: React.FC = () => {
  const { t } = useTranslation();
  const valuesData = (t("about.values", { returnObjects: true }) as ValueItem[]).map(
    (value, index) => ({ ...value, icon: valueIcons[index] }),
  );

  return (
    <Stack
      id={RoutePaths.ABOUT_US}
      width={1}
      direction="column"
      justifyContent="center"
      alignItems="center"
      gap={4}
      sx={{
        scrollMarginTop: 80,
      }}
    >
      <Typography
        color="text.secondary"
        variant="h5"
        fontWeight="light"
        textTransform="uppercase"
        sx={{
          fontSize: { sm: "1.5rem", xs: "1rem" },
        }}
      >
        {t("about.eyebrow")}
      </Typography>

      <Stack
        width={1}
        direction="row"
        gap={8}
        sx={{
          flexDirection: { md: "row", xs: "column" },
        }}
      >
        <Stack
          width={1}
          direction="column"
          justifyContent="flex-start"
          alignItems="flex-start"
          gap={2}
          sx={{
            alignItems: { md: "flex-start", xs: "center" },
          }}
        >
          <Typography
            color="text.secondary"
            variant="h4"
            fontWeight={300}
            sx={{
              fontSize: {
                md: "2.125rem",
                xs: "2rem",
              },
            }}
          >
            {t("about.missionTitlePre")}{" "}
            <Typography
              component="span"
              variant="inherit"
              fontWeight="inherit"
              color="primary"
            >
              {t("about.missionTitleAccent")}
            </Typography>
          </Typography>

          <Typography
            width={0.8}
            color="text.primary"
            variant="body1"
            textAlign="start"
            sx={{
              textAlign: { md: "start", xs: "center" },
            }}
          >
            {t("about.missionBody")}
          </Typography>

          <Button
            variant="outlined"
            onClick={handleBookCall}
            sx={{
              textTransform: "none",
            }}
          >
            {t("about.bookCall")}
          </Button>
        </Stack>

        <Stack
          width={1}
          direction="column"
          justifyContent="flex-start"
          alignItems="flex-start"
          gap={2}
          sx={{
            alignItems: { md: "flex-start", xs: "center" },
          }}
        >
          <Typography
            color="text.secondary"
            variant="h4"
            fontWeight={300}
            sx={{
              fontSize: {
                sm: "2.125rem",
                xs: "2rem",
              },
            }}
          >
            {t("about.valuesTitlePre")}{" "}
            <Typography
              component="span"
              variant="inherit"
              fontWeight="inherit"
              color="primary"
            >
              {t("about.valuesTitleAccent")}
            </Typography>
          </Typography>

          {valuesData.map((value) => (
            <Stack
              key={value.title}
              direction="row"
              justifyContent="flex-start"
              alignItems="center"
              gap={2}
              sx={{
                flexDirection: { md: "row", xs: "column" },
              }}
            >
              {value.icon}

              <Stack
                direction="column"
                justifyContent="flex-start"
                alignItems="flex-start"
                sx={{
                  alignItems: { md: "flex-start", xs: "center" },
                }}
              >
                <Typography color="text.secondary" variant="h6">
                  {value.title}
                </Typography>
                <Typography
                  color="text.primary"
                  variant="body2"
                  sx={{
                    textAlign: { md: "start", xs: "center" },
                  }}
                >
                  {value.description}
                </Typography>
              </Stack>
            </Stack>
          ))}
        </Stack>
      </Stack>
    </Stack>
  );
};
